import { useEffect, useState, useCallback } from 'react'
import { io, Socket } from 'socket.io-client'
import type { GameState, Player, BoardCell, ChatMessage } from '../types/game.types'
import { sessionManager } from '../utils/sessionManager'

interface UseGameSocketReturn {
  socket: Socket | null
  gameState: GameState | null
  isConnected: boolean
  isReconnecting: boolean
  joinGame: (playerName: string, theme: string, packId?: string, maxPlayers?: number, roomCode?: string, forceCreate?: boolean) => void
  startGame: (roomId: string, hostId: string) => void
  addBot: (difficulty?: 'EASY' | 'MEDIUM' | 'HARD') => void
  performAction: (action: {
    type: 'A' | 'B'
    cardId: string
    cellId: string
    narrative?: string
    guess?: string
  }) => void
  surrenderGame: () => void
  leaveGame: () => void
  spyAlert: { message: string; targetCell: BoardCell } | null
  clearSpyAlert: () => void
  error: string | null
  clearError: () => void
  updatePlayerTheme: (playerId: string, theme: string, validated: boolean) => void
  playCard: (data: { cardType: 'HYPNOSE' | 'LAPSUS'; targetCellId?: string; targetPlayerId?: string }) => void
  // Chat System
  chatMessages: ChatMessage[]
  sendChatMessage: (message: string) => void
  unreadMessagesCount: number
  markChatAsRead: () => void
}

const SOCKET_SERVER_URL = (window as any).env?.VITE_API_URL || (import.meta as any).env.VITE_API_URL || 'http://localhost:5001'

export const useGameSocket = (): UseGameSocketReturn => {
  const [socket, setSocket] = useState<Socket | null>(null)
  const [gameState, setGameState] = useState<GameState | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [isReconnecting, setIsReconnecting] = useState(false)
  const [spyAlert, setSpyAlert] = useState<{ message: string; targetCell: BoardCell } | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Chat State
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0)

  // Initialisation de la connexion Socket.IO
  useEffect(() => {
    const socketInstance = io(SOCKET_SERVER_URL, {
      transports: ['websocket', 'polling'],
      timeout: 5000,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    })

    setSocket(socketInstance)

    // Événements de connexion
    socketInstance.on('connect', () => {
      console.log('🔌 Connecté au serveur de jeu')
      setIsConnected(true)
      setError(null)

      // Vérifier s'il y a une partie à récupérer (Ticket de Vestiaire)
      const reconnectionData = sessionManager.getReconnectionData()
      if (reconnectionData) {
        console.log('🎫 Tentative de reconnexion automatique:', reconnectionData)
        setIsReconnecting(true)

        // Émettre la demande de reconnexion
        socketInstance.emit('REQUEST_RECONNECT', {
          playerId: reconnectionData.playerId,
          roomId: reconnectionData.roomId,
          playerName: reconnectionData.playerName,
          playerTheme: reconnectionData.playerTheme,
          timestamp: Date.now()
        })
      }
    })

    socketInstance.on('disconnect', (reason) => {
      console.log('🔌 Déconnecté du serveur de jeu:', reason)
      setIsConnected(false)

      // Si on a une session active, on va tenter de reconnecter
      if (sessionManager.hasActiveGame()) {
        console.log('🔄 Partie active détectée, reconnexion automatique en cours...')
        setIsReconnecting(true)
      }
    })

    socketInstance.on('reconnect_attempt', (attemptNumber) => {
      console.log(`🔄 Tentative de reconnexion ${attemptNumber}/5...`)
      setIsReconnecting(true)
    })

    socketInstance.on('reconnect_failed', () => {
      console.log('❌ Échec de toutes les tentatives de reconnexion')
      setIsReconnecting(false)
      setError('Impossible de se reconnecter au serveur. Veuillez rafraîchir la page.')
    })

    socketInstance.on('connect_error', (err) => {
      console.error('❌ Erreur de connexion:', err)
      setError(`Erreur de connexion: ${err.message}`)
      setIsConnected(false)
    })

    // FIX: Listen to generic server errors
    socketInstance.on('error', (err: any) => {
      console.error('❌ Server Error:', err)
      setError(typeof err === 'string' ? err : err.message || 'Erreur inconnue')
      setIsReconnecting(false)
    })

    // Événements du jeu
    socketInstance.on('GAME_STATE_UPDATE', (newGameState: GameState) => {
      // Ignorer les mises à jour si on n'est pas censé être en jeu (ex: après abandon)
      if (!sessionManager.hasActiveGame()) {
        console.log('🔇 Update ignoré (partie terminée localement)')
        return
      }

      console.log('🎮 État de jeu mis à jour:', newGameState)
      console.log('🎮 Board reçu:', newGameState.board?.length || 'undefined')
      console.log('🎮 Joueurs dans le gameState:', Object.keys(newGameState.players || {}))
      setGameState(newGameState)
    })

    socketInstance.on('PLAYER_JOINED', (player: Player) => {
      console.log('👤 Joueur rejoint:', player.name)
    })

    socketInstance.on('PLAYER_LEFT', (playerId: string) => {
      console.log('👤 Joueur parti:', playerId)
    })

    socketInstance.on('SPY_DATA_REVEALED', (data: { message: string; targetCell: BoardCell }) => {
      console.log('🕵️ Données d\'espion révélées:', data)
      setSpyAlert(data)
      // Auto-clear après 5 secondes
      setTimeout(() => setSpyAlert(null), 5000)
    })



    // Événement de message global (Admin Broadcast)
    socketInstance.on('GLOBAL_MESSAGE', (data: { message: string; type: string }) => {
      console.log('📢 Message global reçu:', data)
      // Utiliser un toast personnalisé pour les annonces globales
      import('react-hot-toast').then(({ toast }) => {
        toast(data.message, {
          icon: '📢',
          duration: 6000,
          style: {
            background: '#4c1d95', // Violet foncé
            color: '#fff',
            border: '2px solid #a78bfa',
            padding: '16px',
            fontWeight: 'bold',
          },
        })
      })
    })

    // Écouter les actions des bots pour feedback visuel
    socketInstance.on('bot-action', (data: { type: string, botName: string, message: string }) => {
      console.log('🤖 Bot Action:', data)
      import('react-hot-toast').then(({ toast }) => {
        toast(data.message, {
          icon: '🤖',
          duration: 4000,
          style: {
            background: '#1e293b', // Slate 800
            color: '#fbbf24', // Amber 400
            border: '1px solid #fbbf24',
          },
        })
      })
    })

    // Événements de reconnexion
    socketInstance.on('GAME_RECOVERY', (recoveredGameState: GameState) => {
      console.log('🔄 Partie récupérée avec succès:', recoveredGameState)
      setGameState(recoveredGameState)
      setIsReconnecting(false)
      setError(null)

      // Message de succès temporaire
      setTimeout(() => {
        console.log('✅ Reconnexion terminée - Vous pouvez continuer à jouer !')
      }, 1000)
    })

    socketInstance.on('RECONNECT_FAILED', (reason: { message: string }) => {
      console.warn('❌ Échec de la reconnexion:', reason.message)
      setIsReconnecting(false)
      setError(`Reconnexion impossible: ${reason.message}`)
      // Nettoyer la session car la partie n'existe plus
      sessionManager.endGame()
    })

    // 🎮 Événements START_GAME
    socketInstance.on('GAME_STARTED', (gameState: GameState) => {
      console.log('🎮 Partie démarrée (V2 Legacy):', gameState)
      setGameState(gameState)
      setError(null)
    })

    // 🌟 Événements V3 (MemoryEngine)
    socketInstance.on('V3_GAME_STARTED', (data: { activePlayerId: string, state: any }) => {
      console.log('🌟 V3 GAME STARTED:', data);
      // On transforme l'event V3 en un GameState compatible ou on le stocke séparément
      // Pour l'instant, on hack le gameState pour indiquer le status 'PLAYING'
      // et on stocke les données V3 dans une propriété custom (à ajouter au type ou via un autre state)
      setGameState({
        id: data.state.roomId,
        status: 'PLAYING',
        players: {}, // Todo: Mapper V3 players to V2 compatible or use V3 state
        currentPlayerId: data.activePlayerId,
        v3State: data.state // Requires type update
      } as any);
      setError(null);
    });

    socketInstance.on('V3_GAME_UPDATE', (state: any) => {
      console.log('🌟 V3 GAME UPDATE:', state);
      setGameState(prev => ({
        ...prev!,
        status: state.phase, // 'PLAYING' | 'VERDICT'
        currentPlayerId: state.activePlayerId,
        v3State: state
      } as any));
    });

    socketInstance.on('START_GAME_FAILED', (reason: { message: string }) => {
      console.warn('❌ Échec du démarrage:', reason.message)
      setError(`Impossible de démarrer: ${reason.message}`)
    })

    // 💬 CHAT EVENTS
    socketInstance.on('chat:broadcast', (msg: ChatMessage) => {
      console.log('💬 Chat Message:', msg)
      setChatMessages(prev => [...prev, msg])
      setUnreadMessagesCount(prev => prev + 1)
    });

    // Nettoyage à la déconnexion
    return () => {
      socketInstance.disconnect()
    }
  }, [])

  // Chat Methods
  const sendChatMessage = useCallback((message: string) => {
    if (socket && isConnected) {
      const roomId = gameState?.id; // Get room ID from state
      if (roomId) {
        socket.emit('chat:message', { roomId, message });
      }
    }
  }, [socket, isConnected, gameState]);

  const markChatAsRead = useCallback(() => {
    setUnreadMessagesCount(0);
  }, []);

  // Fonction pour rejoindre une partie
  const joinGame = useCallback((playerName: string, theme: string, packId?: string, maxPlayers?: number, roomCode?: string, forceCreate?: boolean) => {
    if (socket && isConnected) {
      const playerId = sessionManager.getPlayerId()
      console.log('🎯 Tentative de rejoindre la partie:', { playerId, playerName, theme, packId, maxPlayers, roomCode, forceCreate })

      socket.emit('JOIN_GAME', {
        playerId,
        playerName,
        theme,
        packId, // Envoyer le packId au serveur
        maxPlayers, // Envoyer le nombre max de joueurs
        roomCode, // Envoyer le code de la salle pour rejoindre
        create: forceCreate, // Forcer la création
        timestamp: Date.now()
      })

      // Écouter la confirmation de rejoindre pour stocker la session
      socket.once('GAME_JOINED', (data: { roomId: string }) => {
        sessionManager.startGame(data.roomId, playerName, theme)
        console.log('✅ Partie rejointe, session sauvegardée')
      })
    } else {
      console.warn('⚠️ Socket non connecté, impossible de rejoindre la partie')
      setError('Connexion au serveur requise')
    }
  }, [socket, isConnected])

  // 🎮 Fonction pour démarrer la partie (hôte seulement)
  const startGame = useCallback((roomId: string, hostId: string) => {
    if (socket && isConnected) {
      console.log('🚀 Demande de démarrage de partie:', { roomId, hostId })
      socket.emit('START_GAME', {
        roomId,
        hostId,
        timestamp: Date.now()
      })
    } else {
      console.warn('⚠️ Socket non connecté, impossible de démarrer la partie')
      setError('Connexion au serveur requise')
    }
  }, [socket, isConnected])

  // Fonction pour effectuer une action
  const performAction = useCallback((action: {
    type: 'A' | 'B'
    cardId: string
    cellId: string
    narrative?: string
    guess?: string
  }) => {
    if (socket && isConnected) {
      console.log('🎯 Envoi d\'action:', action)
      socket.emit('PERFORM_ACTION', {
        ...action,
        timestamp: Date.now()
      })
    } else {
      console.warn('⚠️ Socket non connecté, impossible d\'effectuer l\'action')
      setError('Connexion au serveur requise')
    }
  }, [socket, isConnected])

  // Fonction pour ajouter un bot (Mode Solo)
  const addBot = useCallback((difficulty: 'EASY' | 'MEDIUM' | 'HARD' = 'EASY') => {
    if (socket && isConnected) {
      console.log('🤖 Ajout du bot:', difficulty)
      socket.emit('ADD_BOT', {
        difficulty,
        timestamp: Date.now()
      })

      // Écouter la réponse du serveur
      socket.once('bot-added', (data) => {
        console.log('✅ Bot ajouté côté serveur:', data)
      })

      socket.once('error', (error) => {
        // Ignorer l'erreur si on n'est plus dans une partie active (ex: après abandon)
        if (!sessionManager.hasActiveGame()) {
          console.log('🔇 Erreur bot ignorée (partie terminée):', error)
          return
        }
        console.error('❌ Erreur ajout bot:', error)
        setError(`Erreur ajout bot: ${error}`)
      })
    } else {
      console.warn('⚠️ Socket non connecté, impossible d\'ajouter le bot')
      setError('Connexion au serveur requise')
    }
  }, [socket, isConnected])

  // Fonction pour abandonner la partie
  const surrenderGame = useCallback(() => {
    // 1. Tenter de prévenir le serveur si on est connecté
    if (socket && isConnected) {
      console.log('🏳️ Abandon de la partie (envoi serveur)')
      socket.emit('SURRENDER', {
        playerId: sessionManager.getPlayerId(),
        timestamp: Date.now()
      })
    } else {
      console.warn('⚠️ Socket non connecté, abandon local uniquement')
    }

    // 2. Nettoyage local INCONDITIONNEL
    // On nettoie toujours, même si pas connecté, pour ne pas bloquer l'utilisateur
    sessionManager.endGame()
    setGameState(null)
    setError(null)
    setIsReconnecting(false)

    console.log('✅ État local nettoyé après abandon (Forcé)')
  }, [socket, isConnected])

  // Fonction pour effacer l'alerte d'espion
  const clearSpyAlert = useCallback(() => {
    setSpyAlert(null)
  }, [])

  // Fonction pour quitter le lobby (similaire à surrender mais plus générique)
  const leaveGame = useCallback(() => {
    // 1. Emit leave if connected
    if (socket && isConnected) {
      console.log('👋 Quitter la partie/lobby')
      socket.emit('LEAVE_GAME', { // Verify if server supports LEAVE_GAME or just disconnect
        playerId: sessionManager.getPlayerId(),
        timestamp: Date.now()
      })
    }

    // 2. Clean local
    sessionManager.endGame()
    setGameState(null)
    setError(null)
    setIsReconnecting(false)
  }, [socket, isConnected])

  // Fonction pour effacer manuellement les erreurs
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    socket,
    gameState,
    isConnected,
    isReconnecting,
    joinGame,
    startGame,
    addBot,
    performAction,
    surrenderGame,
    leaveGame,
    spyAlert,
    clearSpyAlert,
    error,
    clearError,
    updatePlayerTheme: useCallback((playerId: string, theme: string, validated: boolean) => {
      if (socket && isConnected) {
        console.log('📝 Mise à jour du thème:', { playerId, theme, validated })
        socket.emit('UPDATE_THEME', {
          playerId,
          theme,
          validated,
          timestamp: Date.now()
        })
      }
    }, [socket, isConnected]),

    playCard: useCallback((data: { cardType: 'HYPNOSE' | 'LAPSUS'; targetCellId?: string; targetPlayerId?: string }) => {
      if (socket && isConnected) {
        console.log('🃏 Jouer Carte Spéciale:', data)
        socket.emit('PLAY_CARD', {
          ...data,
          timestamp: Date.now()
        })
      }
    }, [socket, isConnected]),

    // Chat
    chatMessages,
    sendChatMessage,
    unreadMessagesCount,
    markChatAsRead
  }
}
